import React, { useEffect } from 'react';

/**
 * BookingTour (Jose 2026-10-08) — the "live blinking walkthrough" for the web booking path (Way 2 of
 * the /app launch page). Activated by /book?tour=1. It rides the page's existing `currentStep` state
 * machine (barber → service → datetime → info → ready), so it advances AS the client actually books:
 * it blinks a copper ring around the current step's section (via [data-tour=...]), scrolls it into
 * view, and shows a can't-miss bottom coach bar ("Step N of 5 — tap your barber 👆"). Built for
 * tappers-not-readers: point at the real control, advance when they do it. No extra deps.
 */

const STEPS: Record<string, { n: number; hint: string; dir: string }> = {
  barber:   { n: 1, hint: 'Tap your barber', dir: '👆' },
  service:  { n: 2, hint: 'Pick your service — you can add more than one', dir: '👆' },
  datetime: { n: 3, hint: 'Pick a day, then a time', dir: '👆' },
  info:     { n: 4, hint: 'Add your name, phone & email', dir: '👆' },
  ready:    { n: 5, hint: 'Tap “Confirm Booking” — you’re done!', dir: '👇' },
};
const TOTAL = 5;

export const BookingTour: React.FC<{ step: string; active: boolean }> = ({ step, active }) => {
  useEffect(() => {
    if (!active) return;
    const el = document.querySelector(`[data-tour="${step}"]`) as HTMLElement | null;
    if (!el) return;
    el.classList.add('tour-spotlight');
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return () => { el.classList.remove('tour-spotlight'); };
  }, [step, active]);

  if (!active || !STEPS[step]) return null;
  const s = STEPS[step];

  return (
    <>
      <style>{`
        /* Double ring = LOUD on any background (Jose 2026-10-08): white separator (pops on the dark
           copper Confirm button) + solid copper (pops on white tiles), both always visible, + a big
           fading halo that pulses outward. */
        .tour-spotlight{animation:tourRing 1.05s ease-in-out infinite;border-radius:18px}
        @keyframes tourRing{
          0%,100%{box-shadow:0 0 0 3px #fff,0 0 0 7px rgba(156,95,40,1),0 0 0 10px rgba(156,95,40,.45)}
          50%    {box-shadow:0 0 0 3px #fff,0 0 0 7px rgba(156,95,40,1),0 0 0 20px rgba(156,95,40,0)}
        }
        @keyframes tourArrow{0%,100%{transform:translateY(0)}50%{transform:translateY(5px)}}
      `}</style>
      <div className="fixed bottom-4 inset-x-4 z-[60] max-w-lg mx-auto pointer-events-none">
        <div className="rounded-2xl bg-slik-navy text-white shadow-2xl px-5 py-3.5 flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-slik-purple font-extrabold text-lg shrink-0">{s.n}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-white/60 font-bold">Step {s.n} of {TOTAL} · guided</div>
            <div className="text-base font-extrabold leading-tight">
              {s.hint} <span style={{ display: 'inline-block', animation: 'tourArrow 1s ease-in-out infinite' }}>{s.dir}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingTour;
